import { useEffect } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router";
import { useDocumentTitle } from "@/lib/document-title";
import { NotFound } from "./not-found";

/**
 * Last-resort error boundary.
 *
 * Deliberately says nothing about what failed. A stack trace or an exception
 * message on a credit workspace can name a borrower, a table or a query, and
 * this text is rendered in a browser someone may be screen-sharing. Neither
 * `error.message` nor `error.stack` is read anywhere below, and
 * `tests/production-posture.test.ts` asserts that.
 *
 * A 404 thrown by a loader lands here too — a case id that does not exist is
 * not an error worth alarming anyone about, so it renders as "not found".
 */
export function RootErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useDocumentTitle(notFound ? "Not found" : "Something went wrong");

  useEffect(() => {
    if (notFound) return;
    // The seam for an error reporter. Only the shape is logged: the value
    // itself may carry borrower detail, which is the thing not to spread.
    console.error("unhandled_error", {
      kind: isRouteErrorResponse(error) ? `http_${error.status}` : "exception",
    });
  }, [error, notFound]);

  if (notFound) return <NotFound />;

  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="max-w-md text-center">
        <p className="eyebrow">Something went wrong</p>
        <h1 className="mt-2 text-lg font-semibold text-ink">This page could not be shown</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Nothing you were working on has been lost. Try again, and if it keeps happening tell
          us what you were doing.
        </p>
        <button
          type="button"
          onClick={() => void navigate(0)}
          className="mt-4 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white"
        >
          Try again
        </button>
      </div>
    </main>
  );
}
